import { FieldDescription } from "@digdir/designsystemet-react";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import { LexicalComposer, } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import cn from "clsx";
import { $getRoot, $setSelection, createEditor, } from "lexical";
import React, { useState } from "react";
import { Icon } from "../icon/index.js";
import { Label } from "../typography/label/index.js";
import { ValidationMessage } from "../typography/validation-message/index.js";
import CustomOnChangePlugin from "./on-change-plugin.js";
import ReadonlyPlugin from "./readonly-plugin.js";
import ToolbarPlugin from "./toolbar-plugin.js";
const BLOCK_TAGS = new Set([
    "P",
    "DIV",
    "UL",
    "OL",
    "LI",
    "H1",
    "H2",
    "H3",
    "H4",
    "H5",
    "H6",
    "BLOCKQUOTE",
    "TABLE",
    "PRE",
    "HR",
]);
// Wraps bare text/inline nodes in <p> so Lexical can render them
const normalizeDom = (dom) => {
    var _a;
    const { body } = dom;
    let wrapper = null;
    const children = Array.from(body.childNodes);
    for (const child of children) {
        const isBlock = child.nodeType === 1 && BLOCK_TAGS.has(child.tagName);
        const isSpacesOnly = child.nodeType === 3 && /^[ \t]*$/.test((_a = child.textContent) !== null && _a !== void 0 ? _a : "");
        if (isBlock) {
            wrapper = null;
        }
        else if (isSpacesOnly) {
            body.removeChild(child);
        }
        else {
            if (!wrapper) {
                wrapper = dom.createElement("p");
                body.insertBefore(wrapper, child);
            }
            wrapper.appendChild(child);
        }
    }
};
// Utility function to convert HTML string to raw text using Lexical parsing
export const htmlToText = (htmlString) => {
    if (!htmlString)
        return "";
    // Create a temporary editor with the same configuration as the main editor
    const tempEditor = createEditor({
        nodes: [ListNode, ListItemNode, LinkNode],
        onError: () => { },
    });
    let textContent = "";
    tempEditor.update(() => {
        const parser = new DOMParser();
        const dom = parser.parseFromString(htmlString, "text/html");
        normalizeDom(dom);
        const nodes = $generateNodesFromDOM(tempEditor, dom);
        const root = $getRoot();
        root.clear();
        root.append(...nodes);
        textContent = root.getTextContent();
    });
    return textContent;
};
const RemainingCharacters = ({ counter, contentLength, size, }) => {
    if (counter === undefined || counter <= 0)
        return null;
    const exceeded = counter - contentLength < 0;
    return (React.createElement("div", { className: cn("in-rich-text-editor-remaining-characters-container", {
            "in-rich-text-editor-remaining-characters-exceeded": exceeded,
        }) },
        exceeded && (React.createElement(Icon, { size: size === "lg" ? "m" : "s", prefix: "fasr", iconName: "circle-xmark" })),
        React.createElement("p", null, `${Math.abs(counter - contentLength)} ${exceeded ? "tegn for mye" : "tegn igjen"}`)));
};
const RichTextEditor = ({ initialValue, onChange, onBlur, namespace = "DefaultNamespace", readonly, label, description, rows, counter, error, labelId = "rich-text-editor-label", className, ...props }) => {
    const [contentLength, setContentLength] = useState(initialValue ? htmlToText(initialValue).length : 0);
    function onError(error) {
        console.error(error);
    }
    const initialConfig = {
        namespace,
        onError,
        editorState: (editor) => {
            if (!initialValue)
                return;
            editor.update(() => {
                const root = $getRoot();
                root.clear();
                $setSelection(null);
                const parser = new DOMParser();
                const dom = parser.parseFromString(initialValue, "text/html");
                normalizeDom(dom);
                const nodes = $generateNodesFromDOM(editor, dom);
                root.append(...nodes);
                onChange === null || onChange === void 0 ? void 0 : onChange($generateHtmlFromNodes(editor, null));
            });
        },
        nodes: [ListNode, ListItemNode, LinkNode],
        theme: {
            text: {
                bold: cn("in-rich-text-editor-text-bold"),
                italic: cn("in-rich-text-editor-text-italic"),
                underline: cn("in-rich-text-editor-text-underline"),
            },
        },
        editable: !readonly,
    };
    return (React.createElement("div", { "data-size": props["data-size"], className: cn("in-rich-text-editor-outer-container", className) },
        React.createElement("div", null,
            React.createElement(Label, { id: labelId, className: cn("", { "in-rich-text-editor-label": readonly }) }, label),
            React.createElement(FieldDescription, { className: "in-rich-text-editor-description" }, description)),
        React.createElement(LexicalComposer, { initialConfig: initialConfig },
            React.createElement("div", { className: cn("in-rich-text-editor-container", {
                    "in-rich-text-editor-container-readonly": readonly,
                    "in-rich-text-editor-container-error": Boolean(error),
                }) },
                React.createElement(ToolbarPlugin, { readonly: readonly }),
                React.createElement("div", { className: "in-rich-text-editor-input-container" },
                    React.createElement(RichTextPlugin, { contentEditable: React.createElement(ContentEditable, { "aria-labelledby": labelId, className: cn("in-rich-text-editor-input"), onBlur: onBlur, style: { "--rows": rows !== null && rows !== void 0 ? rows : 1 } }), ErrorBoundary: LexicalErrorBoundary })),
                React.createElement(ListPlugin, null),
                React.createElement(CustomOnChangePlugin, { onChange: onChange, setContentLength: setContentLength }),
                React.createElement(ReadonlyPlugin, { readonly: readonly }),
                React.createElement(HistoryPlugin, null))),
        !!error && (React.createElement(ValidationMessage, { "data-size": props["data-size"], "data-color": "danger" }, error)),
        React.createElement(RemainingCharacters, { counter: counter, contentLength: contentLength, size: props["data-size"] })));
};
export { RichTextEditor };
//# sourceMappingURL=rich-text-editor.js.map